import { useState, useEffect, useContext } from 'react'
import EnvContext from '../context/EnvContext'

import { gql, useQuery } from '@apollo/client';
import { THREE_MINUTES } from './pollIntervals'
import { getConnectionEnv } from './env'


const ACCOUNT_QUERY = gql`
query($envName: String!) {
    account(where: {environment: {name: {_eq: $envName}}}) {
        id
        parentId
        accountName
        AOTCTypeId
        AOTCTypeInherited
        disabledByAutoliquidate
        disabledByOrderLimit
        rev
        tradingAllowed
        tradingAllowedSelf
        tradingAllowedSubs
        typeId
        companyId
        environment {
            name
        }
    }
}
`

const ACCOUNT_COMPANY_QUERY = gql`
query($envName: String!) {
    account(where: {environment: {name: {_eq: $envName}}}) {
        id
        companyId
    }
    company(where: {environment: {name: {_eq: $envName}}}) {
        id
        companyName
    }
}
`

// Same join as the accountToCompany view: account.companyId = company.id within one env
export const buildAccountCompanyMap = (accounts, companies) => {
    const companyNames = companies.reduce((acc, c) => {
        acc[c.id] = c.companyName
        return acc
    }, {})
    return accounts.reduce((acc, a) => {
        if (companyNames[a.companyId] !== undefined)
            acc[a.id] = companyNames[a.companyId]
        return acc
    }, {})
}

// Client-side equivalent of the sessionAccountViewAgg view, for sub-envs whose
// accounts live in the parent env and so fall out of the sessionAccountView join
export const aggregateBouncerAccounts = (rows, companyByAccount) => {
    const groups = {}
    rows.forEach(row => {
        const companyName = companyByAccount[row.accountId]
        if (companyName === undefined) return

        const key = [companyName, row.environmentName, row.nodeName, row.nodeIp, row.datacenterName, row.instance].join('|')
        if (!groups[key]) {
            groups[key] = {
                environmentName: row.environmentName,
                nodeName: row.nodeName,
                nodeIp: row.nodeIp,
                datacenterName: row.datacenterName,
                instance: row.instance,
                companyName: companyName,
                up: 0,
                disable: 0,
                unhealthy: 0,
            }
        }
        if (row.status === 'UP') groups[key].up += 1
        else if (row.status === 'DISABLE') groups[key].disable += 1
        else groups[key].unhealthy += 1
    })
    return Object.values(groups)
}

// Account id -> company name, looked up in the env that owns the TTUS accounts.
// Returns null until loaded.
export const useAccountCompany = (env, skip = false) => {

    const [lookupData, setLookupData] = useState(null)
    const { data, loading } = useQuery(ACCOUNT_COMPANY_QUERY, {
        variables: {
            "envName": getConnectionEnv(env),
        },
        pollInterval: THREE_MINUTES,
        skip: skip
    })

    useEffect(() => {
        if (!loading && data) {
            setLookupData(buildAccountCompanyMap(data.account, data.company))
        }
    }, [data, loading])

    return lookupData
}

export const useTTUSAccount = () => {

    const envSelected = useContext(EnvContext)
    const [lookupData, setLookupData] = useState({})
    const { data, loading } = useQuery(ACCOUNT_QUERY, {
        variables: {
            "envName": getConnectionEnv(envSelected),
        },
        pollInterval: THREE_MINUTES
    })



    useEffect(() => {
        if (!loading && data) {
            setLookupData(data.account.reduce((acc, d) => {
                if (d)
                    acc[d.id] = {
                        'id': d.id,
                        'parentId': d.parentId === -1 ? null : d.parentId,
                        'name': d.accountName,
                        'rev': d.rev,
                        'AOTCTypeId': d.AOTCTypeId === -1 ? null : d.AOTCTypeId,
                        'AOTCTypeInherited': d.AOTCTypeInherited === -1 ? null : d.AOTCTypeInherited,
                        'disabledByAutoliquidate': d.disabledByAutoliquidate === -1 ? null : d.disabledByAutoliquidate,
                        'disabledByOrderLimit': d.disabledByOrderLimit === -1 ? null : d.disabledByOrderLimit,
                        'tradingAllowed': d.tradingAllowed === -1 ? null : d.tradingAllowed,
                        'tradingAllowedSelf': d.tradingAllowedSelf === -1 ? null : d.tradingAllowedSelf,
                        'tradingAllowedSubs': d.tradingAllowedSubs === -1 ? null : d.tradingAllowedSubs,
                        'typeId': d.typeId,
                        'companyId': d.companyId,
                        'environment': d.environment.name,
                    }
                return acc
            }, {}))
        }
    }, [data, loading])

    return lookupData
}

export const useTTUSAccountExact = (id) => {

    const ACCOUNT_QUERY_EXACT = gql`
    query($envName: String!, $id: bigint!) {
        account(where: {environment: {name: {_eq: $envName}}, id: {_eq: $id}}) {
            id
            parentId
            accountName
            AOTCTypeId
            AOTCTypeInherited
            disabledByAutoliquidate
            disabledByOrderLimit
            rev
            tradingAllowed
            tradingAllowedSelf
            tradingAllowedSubs
            typeId
            companyId
            environment {
                name
            }
        }
    }
    `

    const envSelected = useContext(EnvContext)
    const [lookupData, setLookupData] = useState({})
    const { data, loading } = useQuery(ACCOUNT_QUERY_EXACT, {
        variables: {
            "envName": getConnectionEnv(envSelected),
            "id": id
        }
    })



    useEffect(() => {
        if (!loading && data) {
            setLookupData(data.account.reduce((acc, d) => {
                if (d)
                    acc[d.id] = {
                        'id': d.id,
                        'parentId': d.parentId === -1 ? null : d.parentId,
                        'name': d.accountName,
                        'rev': d.rev,
                        'AOTCTypeId': d.AOTCTypeId === -1 ? null : d.AOTCTypeId,
                        'AOTCTypeInherited': d.AOTCTypeInherited === -1 ? null : d.AOTCTypeInherited,
                        'disabledByAutoliquidate': d.disabledByAutoliquidate === -1 ? null : d.disabledByAutoliquidate,
                        'disabledByOrderLimit': d.disabledByOrderLimit === -1 ? null : d.disabledByOrderLimit,
                        'tradingAllowed': d.tradingAllowed === -1 ? null : d.tradingAllowed,
                        'tradingAllowedSelf': d.tradingAllowedSelf === -1 ? null : d.tradingAllowedSelf,
                        'tradingAllowedSubs': d.tradingAllowedSubs === -1 ? null : d.tradingAllowedSubs,
                        'typeId': d.typeId,
                        'companyId': d.companyId,
                        'environment': d.environment.name,
                    }
                return acc
            }, {}))
        }
    }, [data, loading])

    return lookupData
}


export const useTTUSParentAccount = () => {

    const envSelected = useContext(EnvContext)
    const [lookupData, setLookupData] = useState({})
    const { data, loading } = useQuery(ACCOUNT_QUERY, {
        variables: {
            "envName": getConnectionEnv(envSelected),
        }, pollInterval: THREE_MINUTES
    })



    useEffect(() => {
        if (!loading && data) {
            setLookupData(data.account.reduce((acc, d) => {
                if (d && d.parentId !== -1)
                    acc[d.parentId] = {
                        'id': d.id,
                        'parentId': d.parentId === -1 ? null : d.parentId,
                        'name': d.accountName,
                        'rev': d.rev,
                        'AOTCTypeId': d.AOTCTypeId === -1 ? null : d.AOTCTypeId,
                        'AOTCTypeInherited': d.AOTCTypeInherited === -1 ? null : d.AOTCTypeInherited,
                        'disabledByAutoliquidate': d.disabledByAutoliquidate === -1 ? null : d.disabledByAutoliquidate,
                        'disabledByOrderLimit': d.disabledByOrderLimit === -1 ? null : d.disabledByOrderLimit,
                        'tradingAllowed': d.tradingAllowed === -1 ? null : d.tradingAllowed,
                        'tradingAllowedSelf': d.tradingAllowedSelf === -1 ? null : d.tradingAllowedSelf,
                        'tradingAllowedSubs': d.tradingAllowedSubs === -1 ? null : d.tradingAllowedSubs,
                        'typeId': d.typeId,
                        'companyId': d.companyId,
                        'environment': d.environment.name,
                    }
                return acc
            }, {}))
        }
    }, [data, loading])

    return lookupData
}

